import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Clock, Rss } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CtaBlock } from "../components/CtaBlock";
import { Picture, findPhoto } from "../lib/photos";
import { useSeo } from "../lib/seo";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [comingSoonCount, setComingSoonCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    let alive = true;
    Promise.all([
      axios.get(`${API}/blog`),
      axios.get(`${API}/photos`),
    ]).then(([b, ph]) => {
      if (!alive) return;
      setPosts(b.data.posts || []);
      setComingSoonCount(b.data.coming_soon_count || 0);
      setCategories(b.data.categories || []);
      setPhotos(ph.data.photos || []);
    });
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    if (!activeCategory) return posts;
    return posts.filter((p) => p.category === activeCategory);
  }, [posts, activeCategory]);

  const PAGE_SIZE = 12;
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const [page, setPageState] = useState(urlPage);
  // Wrap setPage so every page change syncs to the URL (?page=N).
  // Page 1 clears the param so canonical URL stays /blog.
  const setPage = (next) => {
    const value = typeof next === "function" ? next(page) : next;
    setPageState(value);
    const nextParams = new URLSearchParams(searchParams);
    if (value <= 1) nextParams.delete("page");
    else nextParams.set("page", String(value));
    setSearchParams(nextParams, { replace: false });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  // Keep local state in sync when the URL changes (back/forward, deep links)
  useEffect(() => { setPageState(urlPage); }, [urlPage]);
  // Reset to page 1 when category filter CHANGES — but not on the initial
  // mount and not on React StrictMode's second effect invocation, so a
  // deep-linked ?page=5 URL isn't clobbered on first render.
  const lastCategory = useRef(activeCategory);
  useEffect(() => {
    if (lastCategory.current === activeCategory) return;
    lastCategory.current = activeCategory;
    setPageState(1);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("page");
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page],
  );

  const canonical = "https://westbridgehotels.com/blog";
  const breadcrumbs = [{ label: "Blog", to: "/blog" }];
  const jsonLd = useMemo(() => {
    const blogLd = {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Westbridge Inn & Suites — Journal",
      url: canonical,
      publisher: {
        "@type": "Organization",
        name: "Westbridge Inn & Suites",
        url: "https://westbridgehotels.com",
      },
      blogPost: posts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        url: `https://westbridgehotels.com/blog/${p.slug}`,
        datePublished: p.date_published,
        description: p.excerpt,
        author: { "@type": "Organization", name: p.author || "Westbridge Inn & Suites Team" },
      })),
    };
    return [blogLd, buildBreadcrumbLd(breadcrumbs, "https://westbridgehotels.com")];
  }, [posts]);

  useSeo({
    title: "Journal & Local Guides — Westbridge Inn & Suites, Centerville, Iowa",
    description:
      "Honest local guides, traveler tips, and accessibility notes from the front desk at Westbridge Inn & Suites — Rathbun Lake, Centerville square, southern Iowa.",
    canonical,
    jsonLd,
  });

  const [hero, ...rest] = paged;

  return (
    <main id="main" data-testid="blog-page">
      <PageHero
        eyebrow="Journal"
        title="Local guides, traveler tips, and honest notes from the front desk."
        intro={`Real writing about southern Iowa — Rathbun Lake, the Centerville square, driving the Hwy 2 corridor, staying with a big dog, or waking up in a small town on a Saturday. ${comingSoonCount} more pieces in the pipeline.`}
        breadcrumbs={breadcrumbs}
        compact
        testId="blog-hero"
      />

      {/* Category filter */}
      <section className="bg-cream border-b border-tan/60">
        <div className="container-app py-6 flex flex-wrap items-center gap-3" data-testid="blog-filters">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.16em] border transition-colors ${
              !activeCategory
                ? "bg-forest-900 text-cream border-forest-900"
                : "border-tan text-forest-900 hover:border-brass"
            }`}
            data-testid="blog-filter-all"
          >
            All Posts
          </button>
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCategory(c)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.16em] border transition-colors ${
                activeCategory === c
                  ? "bg-forest-900 text-cream border-forest-900"
                  : "border-tan text-forest-900 hover:border-brass"
              }`}
              data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
          <span className="ml-auto text-xs uppercase tracking-[0.16em] text-ink/60 flex items-center gap-2">
            <Rss size={13} className="text-brass" /> {filtered.length} published · {comingSoonCount} coming
          </span>
        </div>
      </section>

      {/* Hero article */}
      {hero && (
        <section className="bg-cream-100 paper-grain py-16 md:py-24">
          <div className="container-app grid md:grid-cols-12 gap-10 md:gap-16 items-center">
            <div className="md:col-span-7 order-2 md:order-1">
              <p className="eyebrow mb-3">{hero.category} · Featured</p>
              <h2 className="font-display font-light text-3xl md:text-5xl leading-[1.05] text-forest-900 tracking-tight">
                <Link
                  to={`/blog/${hero.slug}`}
                  className="hover:text-brass transition-colors"
                  data-testid={`blog-hero-link`}
                >
                  {hero.title}
                </Link>
              </h2>
              <p className="mt-5 text-ink/80 text-lg leading-relaxed max-w-xl">
                {hero.excerpt}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs uppercase tracking-[0.18em] text-ink/60">
                <span>{formatDate(hero.date_published)}</span>
                {hero.read_minutes ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={12} /> {hero.read_minutes} min read
                  </span>
                ) : null}
                <span>{hero.author}</span>
              </div>
              <Link
                to={`/blog/${hero.slug}`}
                className="btn-primary mt-8 inline-flex"
                data-testid="blog-hero-cta"
              >
                Read the article <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="md:col-span-5 order-1 md:order-2">
              <Link to={`/blog/${hero.slug}`} className="block aspect-[4/3] overflow-hidden bg-forest-800">
                <Picture
                  photo={findPhoto(photos, hero.hero_photo_slug)}
                  className="block w-full h-full"
                  imgClassName="w-full h-full object-cover"
                  sizes="(min-width: 768px) 40vw, 100vw"
                  loading="eager"
                />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Card grid */}
      <section className="bg-cream py-20 md:py-28">
        <div className="container-app">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {rest.map((p) => (
              <article
                key={p.slug}
                className="group flex flex-col bg-cream-50 border border-tan hover:border-brass/70 transition-colors"
                data-testid={`blog-card-${p.slug}`}
              >
                <Link to={`/blog/${p.slug}`} className="block aspect-[16/10] overflow-hidden bg-forest-800">
                  <Picture
                    photo={findPhoto(photos, p.hero_photo_slug)}
                    className="block w-full h-full"
                    imgClassName="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                    sizes="(min-width: 1024px) 30vw, (min-width: 768px) 45vw, 100vw"
                  />
                </Link>
                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <p className="eyebrow mb-3">{p.category}</p>
                  <h3 className="font-display text-xl md:text-2xl text-forest-900 leading-tight mb-3">
                    <Link to={`/blog/${p.slug}`} className="hover:text-brass transition-colors">
                      {p.title}
                    </Link>
                  </h3>
                  <p className="text-sm text-ink/75 leading-relaxed mb-5 flex-1">
                    {p.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs uppercase tracking-[0.16em] text-ink/55">
                    <span>{formatDate(p.date_published)}</span>
                    {p.read_minutes ? (
                      <span className="inline-flex items-center gap-1"><Clock size={11} /> {p.read_minutes} min</span>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="text-center text-ink/60 py-16" data-testid="blog-empty">
              No posts in this category yet — check back soon.
            </p>
          )}

          {totalPages > 1 && (
            <nav
              className="mt-14 flex items-center justify-center gap-2"
              aria-label="Journal pagination"
              data-testid="blog-pagination"
            >
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-2 text-xs uppercase tracking-[0.16em] border border-tan text-forest-900 hover:border-brass disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="blog-page-prev"
              >
                ← Prev
              </button>
              {Array.from({ length: totalPages }).map((_, i) => {
                const n = i + 1;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    aria-current={page === n ? "page" : undefined}
                    className={`w-9 h-9 text-sm border transition-colors ${
                      page === n
                        ? "bg-forest-900 text-cream border-forest-900"
                        : "border-tan text-forest-900 hover:border-brass"
                    }`}
                    data-testid={`blog-page-${n}`}
                  >
                    {n}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-2 text-xs uppercase tracking-[0.16em] border border-tan text-forest-900 hover:border-brass disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="blog-page-next"
              >
                Next →
              </button>
            </nav>
          )}

          {comingSoonCount > 0 && !activeCategory && (
            <div className="mt-20 md:mt-28 border-t border-tan/70 pt-12 max-w-3xl">
              <p className="eyebrow mb-3">Editorial pipeline</p>
              <h3 className="font-display font-light text-2xl md:text-3xl text-forest-900 leading-tight">
                {comingSoonCount} more guides in the works — from the crappie run at Rathbun to a wheelchair-accessible day trip through Appanoose County.
              </h3>
              <p className="mt-4 text-ink/70 leading-relaxed">
                We&apos;re publishing more local writing every month. Have something you&apos;d like us to cover? Email{" "}
                <a
                  href="mailto:Centerville@westbridgehotels.com"
                  className="brass-underline text-forest-900"
                  data-testid="blog-suggest-email"
                >
                  Centerville@westbridgehotels.com
                </a>.
              </p>
            </div>
          )}
        </div>
      </section>

      <CtaBlock
        eyebrow="Book direct"
        title="Every guide ends at the same place — the front desk."
        body="Ready to plan a weekend? Our best rate is on our booking page. Call the front desk with any special request; a real person answers, day or night."
      />
    </main>
  );
}
